import { useDraggable, useDroppable } from "@dnd-kit/core";
import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CoverageTier, resolveCoverageTier } from "../../../../components/UIToolkit/Coverage/CoverageDeltaCell";
import { NameChip } from "../../../../components/UIToolkit/Coverage/NameChip";
import { COVERAGE, GRID_FONT, GRID_RADIUS, INK, NEUTRAL, OVERTIME, SURFACE } from "../../../../theme/tokens";
import { EXPANDED_ROW_MIN_H } from "../gridGeometry";
import type { AssignedName, CoverageRow as RowType } from "../types";
import { type GridCellKey, serializeGridCell, useCoverageGridContext } from "./CoverageGridContext";

/** A chip being dragged, with the cell it came from (for a MOVE). */
export type DragData = { from: GridCellKey; assignment: AssignedName };

/** Bodies filling the slot (PTO counts — it holds the recurring slot), for open-slot affordances. */
export const filledCount = (names: AssignedName[]) => names.length;

type DetailCellProps = {
	rowId: string;
	dayIdx: number;
	cell: RowType["days"][number];
	weekIdx: number;
	rowLabel: string;
	dayLabel: string;
	draggingId?: string;
	isDragging: boolean;
	onOpen: () => void;
	/** Live edit: click a chip → edit that nurse's recurring shift. */
	onEditAssignment?: (assignment: AssignedName) => void;
	/** Live create: click an open-slot +Add → create a recurring shift for this row. */
	onCreate?: () => void;
};

/** One Detail-grid day cell: droppable slot holding draggable name chips + open-slot +Add affordances. */
export const DetailCell = memo(
	({ rowId, dayIdx, cell, weekIdx, rowLabel, dayLabel, draggingId, isDragging, onOpen, onEditAssignment, onCreate }: DetailCellProps) => {
		const { t } = useTranslation();
		const { setHoveredNurse } = useCoverageGridContext();
		const tier = resolveCoverageTier(cell);
		const openSlots = useMemo(() => Math.max(0, cell.target - filledCount(cell.names)), [cell]);
		const isNotRequired = tier === CoverageTier.NotRequired && cell.names.length === 0;

		const { setNodeRef, isOver, active } = useDroppable({ id: serializeGridCell({ rowId, dayIdx }) });
		// A drop is only valid if there's an open slot AND it isn't this same cell.
		const dragData = active?.data.current as DragData | undefined;
		const isSelfSource = dragData?.from.rowId === rowId && dragData?.from.dayIdx === dayIdx;
		const canDrop = openSlots > 0 && !isSelfSource;
		// Suppress linked hover during a drag so the two affordances don't fight.
		const onHoverNurse = isDragging ? undefined : setHoveredNurse;

		return (
			<Box
				ref={setNodeRef}
				className="ms3-cell"
				role="gridcell"
				data-week={weekIdx}
				data-testid={`ms3-detail-cell-${rowId}-${dayIdx}`}
				aria-label={`${rowLabel}, ${dayLabel}`}
				sx={(theme) => ({
					position: "relative",
					display: "flex",
					flexWrap: "wrap",
					alignContent: "flex-start",
					gap: 0.375,
					p: 0.5,
					minHeight: EXPANDED_ROW_MIN_H,
					width: "100%",
					backgroundColor: isNotRequired ? COVERAGE.MUTED_BG : SURFACE,
					transition: "background-color 120ms, box-shadow 120ms",
					...(isOver && canDrop
						? {
								backgroundColor: alpha(theme.palette.primary.main, 0.1),
								boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
							}
						: {}),
				})}
			>
				{cell.names.map((n) => (
					<DraggableChip
						key={n.id}
						rowId={rowId}
						dayIdx={dayIdx}
						assignment={n}
						isDraggingThis={draggingId === n.id}
						onHoverNurse={onHoverNurse}
						onEditAssignment={onEditAssignment}
					/>
				))}

				{/* One +Add affordance per missing required slot. Live → create a recurring
			    shift; mock/prototype → fall back to opening the cell popover. */}
				{Array.from({ length: openSlots }, (_, i) => (
					<Box
						// biome-ignore lint/suspicious/noArrayIndexKey: open-slot placeholders have no stable identity
						key={`open-${i}`}
						component="button"
						type="button"
						onClick={onCreate ?? onOpen}
						data-testid={`ms3-detail-add-${rowId}-${dayIdx}-${i}`}
						aria-label={t("schedule.template.v3.popover.add")}
						sx={{
							display: "inline-flex",
							alignItems: "center",
							gap: 0.25,
							px: 0.625,
							py: "2px",
							border: `1px dashed ${NEUTRAL[400]}`,
							borderRadius: GRID_RADIUS.CHIP,
							backgroundColor: "transparent",
							color: INK.MUTED,
							fontSize: GRID_FONT.XS,
							fontWeight: 600,
							cursor: "pointer",
							transition: "background-color 120ms, border-color 120ms",
							"@media (hover: hover) and (pointer: fine)": {
								"&:hover": { borderColor: NEUTRAL[500], backgroundColor: NEUTRAL[100], color: INK.SECONDARY },
							},
						}}
					>
						<AddIcon sx={{ fontSize: 12 }} />
						{t("schedule.template.v3.detail.add")}
					</Box>
				))}

				{cell.names.length === 0 && openSlots === 0 && (
					<Box
						sx={{
							width: "100%",
							height: `calc(${EXPANDED_ROW_MIN_H} - 8px)`,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: NEUTRAL[400],
							fontSize: GRID_FONT.MD,
						}}
					>
						·
					</Box>
				)}
			</Box>
		);
	},
);

const DraggableChip = ({
	rowId,
	dayIdx,
	assignment,
	isDraggingThis,
	onHoverNurse,
	onEditAssignment,
}: {
	rowId: string;
	dayIdx: number;
	assignment: AssignedName;
	isDraggingThis: boolean;
	/** Set the hovered nurse id (or `null` on leave); `undefined` disables linked hover (e.g. mid-drag). */
	onHoverNurse?: (id: number | null) => void;
	/** Click (not drag) → edit this nurse's recurring shift. */
	onEditAssignment?: (assignment: AssignedName) => void;
}) => {
	const { t } = useTranslation();
	const data: DragData = { from: { rowId, dayIdx }, assignment };
	const { attributes, listeners, setNodeRef } = useDraggable({
		id: `${rowId}::${dayIdx}::${assignment.id}`,
		data,
		disabled: assignment.onPto,
	});

	const ariaParts = [assignment.name];
	if (assignment.charge) ariaParts.push(t("schedule.template.v3.detail.chargeAria"));
	if (assignment.ot) ariaParts.push(t("schedule.template.v3.people.otBadge"));
	if (assignment.onPto) ariaParts.push(t("schedule.template.v3.popover.onPto"));

	// A non-PTO chip is editable when wiring is present. dnd-kit's PointerSensor has
	// a 4px activation distance, so a plain click (no drag) falls through to onClick.
	const editable = !!onEditAssignment && !assignment.onPto;

	return (
		<Box
			ref={setNodeRef}
			{...(assignment.onPto ? {} : listeners)}
			{...attributes}
			onClick={editable ? () => onEditAssignment?.(assignment) : undefined}
			sx={{ position: "relative", display: "inline-flex", minWidth: 0, maxWidth: "100%", cursor: editable ? "pointer" : undefined }}
		>
			<NameChip
				name={assignment.name}
				charge={assignment.charge}
				chargeLabel={t("schedule.template.v3.detail.charge")}
				onPto={assignment.onPto}
				showDragHandle={!assignment.onPto}
				isDragging={isDraggingThis}
				linkedHoverGroupId={assignment.nurseId}
				onHoverChange={onHoverNurse ? (hovered) => onHoverNurse(hovered ? assignment.nurseId : null) : undefined}
				ariaLabel={ariaParts.join(", ")}
				dataTestId={`ms3-detail-chip-${assignment.id}`}
			/>
			{/* Corner-float the OT badge so it never steals inline width from the name in a narrow cell. */}
			{assignment.ot && (
				<Box
					component="span"
					aria-hidden
					data-testid={`ms3-detail-ot-${assignment.id}`}
					sx={{
						position: "absolute",
						top: -4,
						right: -3,
						zIndex: 1,
						pointerEvents: "none",
						fontSize: 8,
						fontWeight: 800,
						lineHeight: 1.3,
						letterSpacing: "0.02em",
						color: OVERTIME.INK,
						backgroundColor: OVERTIME.BG,
						px: 0.375,
						borderRadius: 0.5,
						border: `1px solid ${SURFACE}`,
						boxShadow: `0 1px 2px ${NEUTRAL[300]}`,
						// Approaching-OT is the softer signal; over-OT owns the full weight.
						opacity: assignment.ot === "approaching" ? 0.72 : 1,
					}}
				>
					{t("schedule.template.v3.people.otBadge")}
				</Box>
			)}
		</Box>
	);
};
